'use client';

import { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { SlidersHorizontal, Zap } from 'lucide-react';
import { ListingTypeItem } from '@/hotdesk-ui/store/slices/listing-types.slice';
import { HEADER_HEIGHT } from '@/hotdesk-ui/constants/layout.constant';
import styles from './ExploreFilterChips.module.scss';
export interface ExploreFilterChipsProps {
  listingTypes: ListingTypeItem[];
  listingTypesLoading: boolean;
  /** Comma-separated selected type ids (from the URL / redux filters). */
  typeIn?: string;
  onOpenFilters: () => void;
  activeFilterCount: number;
  onSelectWorkspaces: () => void;
  onSelectType: (typeId: number) => void;
  instantBooking: boolean;
  onToggleInstantBooking: () => void;
}

/**
 * The row under the header: a Filters button and the Instant booking toggle,
 * followed by the workspace-type chips. Replaces the old centred hero pills —
 * the location heading moved to the results row.
 */
const ExploreFilterChips: FC<ExploreFilterChipsProps> = ({
  listingTypes,
  listingTypesLoading,
  typeIn,
  onOpenFilters,
  activeFilterCount,
  onSelectWorkspaces,
  onSelectType,
  instantBooking,
  onToggleInstantBooking
}) => {
  const t = useTranslations();

  // This row is pinned under the header and the results row pins directly
  // beneath it, so publish where that row's top edge belongs: the header's
  // height plus this row's measured height. Measured rather than assumed,
  // because chip height moves with translated labels.
  //
  // Published as a finished pixel value, NOT as an ingredient for a calc()
  // on the other end: if a custom property inside a calc() ever resolves to
  // something invalid, the whole expression is invalid, `top` falls back to
  // `auto`, and the results row silently stops sticking altogether. A bare
  // var() with a fallback degrades to the fallback instead.
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const root = document.documentElement;
    const apply = () => {
      const height = row.getBoundingClientRect().height;
      // Never publish garbage — a bad value here unsticks the row.
      if (!Number.isFinite(height) || height <= 0) return;
      // Publish the exact measured height — rounding the sum upward leaves
      // a subpixel seam between the two sticky rows while cards scroll.
      root.style.setProperty('--explore-chips-height', `${height}px`);
      root.style.setProperty('--explore-results-top', `${HEADER_HEIGHT + height}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(row);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--explore-chips-height');
      root.style.removeProperty('--explore-results-top');
    };
  }, []);
  const selectedTypes = useMemo(() => typeIn ? typeIn.split(',').filter(Boolean).map(Number) : [], [typeIn]);
  return <div className={styles.row} ref={rowRef} data-sentry-component="ExploreFilterChips" data-sentry-source-file="ExploreFilterChips.tsx">
            <div className={styles.rowInner}>
                <button type="button" className={`${styles.filtersChip} ${activeFilterCount > 0 ? styles.filtersChipActive : ''}`} onClick={onOpenFilters}>
                    <SlidersHorizontal size={14} data-sentry-element="SlidersHorizontal" data-sentry-source-file="ExploreFilterChips.tsx" />
                    <span>{t('filters.filters')}</span>
                    {activeFilterCount > 0 && <span className={styles.filtersCount}>
                            {activeFilterCount}
                        </span>}
                </button>

                <button type="button" aria-pressed={instantBooking} className={`${styles.chip} ${styles.iconChip} ${instantBooking ? styles.chipActive : ''}`} onClick={onToggleInstantBooking}>
                    {/* Same filled bolt as the card's Instant booking badge;
                        follows the chip's text colour. */}
                    <Zap size={14} fill="currentColor" aria-hidden="true" data-sentry-element="Zap" data-sentry-source-file="ExploreFilterChips.tsx" />
                    <span>{t('filters.suggested.instantBooking')}</span>
                </button>

                <button type="button" className={`${styles.chip} ${selectedTypes.length === 0 ? styles.chipActive : ''}`} onClick={onSelectWorkspaces}>
                    {t('landing-v4.categories.workspaces')}
                </button>

                {listingTypesLoading && listingTypes.length === 0 ? [1, 2, 3, 4].map(i => <span key={i} className={styles.chipSkeleton} />) : listingTypes.map(lt => <button key={lt.id} type="button" className={`${styles.chip} ${selectedTypes.includes(lt.id) ? styles.chipActive : ''}`} onClick={() => onSelectType(lt.id)}>
                              {lt.type}
                          </button>)}
            </div>
        </div>;
};
export default ExploreFilterChips;